'use client';
import { alpha, createTheme, Theme } from '@mui/material/styles';
import { Shadows as MuiShadows } from '@mui/material/styles/shadows';
import { Lato } from 'next/font/google';
import { CSSProperties } from 'react';

const lato = Lato({
  weight: ['300', '400', '700'],
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-lato',
});

export interface CustomShadows extends MuiShadows {
  a: string;
  b: string;
  c: string;
  d: string;
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
  }

  interface TypographyVariants {
    display: CSSProperties;
    label1: CSSProperties;
    label2: CSSProperties;
    pill: CSSProperties & { strong?: CSSProperties };
    pill2: CSSProperties & { strong?: CSSProperties };
    tiny: CSSProperties;
    small: CSSProperties;
    medium: CSSProperties;
    large: CSSProperties;
    muted: CSSProperties;
  }

  interface TypographyVariantsOptions {
    display?: CSSProperties;
    label1?: CSSProperties;
    label2?: CSSProperties;
    pill?: CSSProperties & { strong?: CSSProperties };
    pill2?: CSSProperties & { strong?: CSSProperties };
    tiny?: CSSProperties;
    small?: CSSProperties;
    medium?: CSSProperties;
    large?: CSSProperties;
    muted?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
    label1: true;
    label2: true;
    pill: true;
    pill2: true;
    tiny: true;
    small: true;
    medium: true;
    large: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    flat: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    'outlined-fine': true;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50?: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    650: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteOptions {
    brand?: {
      dark: string;
      light: string;
      brandDark: string;
      brand: string;
      primaryDark: string;
      primary: string;
      primarySecondary1: string;
      primarySecondary2: string;
      primarySecondary3: string;
      secondary: string;
      secondary1: string;
      magentaGlow: string;
      pinkHalo: string;
      green: string;
      greenLight: string;
      gray: string;
      grayLight: string;
      bodyText: string;
      error: string;
    };
    gradients?: {
      gradient1?: string;
      gradient2?: string;
      gradient3?: string;
      gradient4?: string;
      gradientCta?: string;
      heroText?: string;
      imageOverlay?: string;
      imageOverlayThumbnail?: string;
    };
    purple?: ColorRange;
  }

  interface Palette {
    common: {
      black: string;
      white: string;
    };
    brand: {
      dark: string;
      light: string;
      brandDark: string;
      brand: string;
      primaryDark: string;
      primary: string;
      primarySecondary1: string;
      primarySecondary2: string;
      primarySecondary3: string;
      secondary: string;
      secondary1: string;
      magentaGlow: string;
      pinkHalo: string;
      green: string;
      greenLight: string;
      gray: string;
      grayLight: string;
      bodyText: string;
      error: string;
    };
    gradients: {
      gradient1: string;
      gradient2: string;
      gradient3: string;
      gradient4: string;
      gradientCta: string;
      purpleWhite: string;
      purpleLight: string;
      purpleDark: string;
      primaryDark: string;
      reviewCard: string;
      secondaryBrand: string;
      heroText: string;
      imageOverlay: string;
      imageOverlayThumbnail: string;
    };
    purple: ColorRange;
  }
}

export const GradientText =
  'linear-gradient(to right, rgba(137,104,253,1) 0%, rgba(203,67,173,1) 100%)';

export const gradientTextCss = (theme: Theme, gradient = 'gradient1') => ({
  fontWeight: 'normal',
  background: (theme.palette.gradients as { [k: string]: string })?.[gradient],
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

export const getTypographyModifierClasses = (theme: Theme) => {
  return {
    '.cms-mb-0': { marginBottom: `0 !important` },
    '.cms-mt-0': { marginTop: `0 !important` },
    '.cms-mb-xs': { marginBottom: `${theme.spacing(1)} !important` },
    '.cms-mt-xs': { marginTop: `${theme.spacing(1)} !important` },
    '.cms-mb-sm': { marginBottom: `${theme.spacing(2)} !important` },
    '.cms-mt-sm': { marginTop: `${theme.spacing(2)} !important` },
    '.cms-mb-md': { marginBottom: `${theme.spacing(4)} !important` },
    '.cms-mt-md': { marginTop: `${theme.spacing(4)} !important` },
    '.cms-mb-lg': { marginBottom: `${theme.spacing(8)} !important` },
    '.cms-mt-lg': { marginTop: `${theme.spacing(8)} !important` },
    '.cms-mb-xl': { marginBottom: `${theme.spacing(12)} !important` },
    '.cms-mt-xl': { marginTop: `${theme.spacing(12)} !important` },
    '.cms-ta-center': { textAlign: 'center !important' },
    '.cms-ta-left': { textAlign: 'left !important' },
    '.cms-ta-right': { textAlign: 'right !important' },
    '.cms-fs-tiny': {
      fontSize: `${theme.typography.tiny?.fontSize} !important`,
      lineHeight: `${theme.typography.tiny?.lineHeight} !important`,
    },
    '.cms-fs-small': {
      fontSize: `${theme.typography.small?.fontSize} !important`,
      lineHeight: `${theme.typography.small?.lineHeight} !important`,
    },
    '.cms-fs-medium': {
      fontSize: `${theme.typography.medium?.fontSize} !important`,
      lineHeight: `${theme.typography.medium?.lineHeight} !important`,
    },
    '.cms-fs-large': {
      fontSize: `${theme.typography.large?.fontSize} !important`,
      lineHeight: `${theme.typography.large?.lineHeight} !important`,
    },
    '.cms-fs-h1': {
      fontSize: `${theme.typography.h1?.fontSize} !important`,
      lineHeight: `${theme.typography.h1?.lineHeight} !important`,
    },
    '.cms-fs-h2': {
      fontSize: `${theme.typography.h2?.fontSize} !important`,
      lineHeight: `${theme.typography.h2?.lineHeight} !important`,
    },
    '.cms-fs-h3': {
      fontSize: `${theme.typography.h3?.fontSize} !important`,
      lineHeight: `${theme.typography.h3?.lineHeight} !important`,
    },
    '.cms-fs-h4': {
      fontSize: `${theme.typography.h4?.fontSize} !important`,
      lineHeight: `${theme.typography.h4?.lineHeight} !important`,
    },
    '.cms-fs-h5': {
      fontSize: `${theme.typography.h5?.fontSize} !important`,
      lineHeight: `${theme.typography.h5?.lineHeight} !important`,
    },
    '.cms-fs-h6': {
      fontSize: `${theme.typography.h6?.fontSize} !important`,
      lineHeight: `${theme.typography.h6?.lineHeight} !important`,
    },
  };
};

const getHeadingDecorators = (theme: Theme, small = false) => ({
  zIndex: 1,
  position: 'relative' as const,
  strong: small
    ? {
        ...gradientTextCss(theme),
        '&::before': {
          padding: 0,
          display: 'none',
          p: 0,
        },
      }
    : {
        ...gradientTextCss(theme),
        display: 'inline-block',
        textTransform: 'uppercase',
        padding: '0.3em 0.6em',
        lineHeight: 1,
        borderRadius: '30rem',
        verticalAlign: 'middle',
        boxSizing: 'content-box',
        whiteSpace: 'nowrap',
        fontSize: '0.75em',
        position: 'relative',
        fontStyle: 'normal',
        fontWeight: 700,
        marginTop: '-0.05em',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: theme.palette.purple[900],
          opacity: 0.9,
          zIndex: -1,
          borderRadius: '30rem',
        },
      },
  em: {
    color: theme.palette.brand.primary,
    fontStyle: 'normal',
    '.background-primary & ': {
      color: alpha(theme.palette.common.white, 0.62),
    },
    '& > strong': small
      ? {}
      : {
          color: theme.palette.common.black,
          WebkitBackgroundClip: 'unset',
          WebkitTextFillColor: 'unset',
          '&::before': {
            background: theme.palette.gradients.gradient1,
          },
        },
  },
  u: {
    color: theme.palette.brand.secondary,
    fontStyle: 'normal',
    textDecoration: 'none',
    '& > strong': small
      ? {}
      : {
          WebkitBackgroundClip: 'unset',
          WebkitTextFillColor: 'unset',
          background: `${theme.palette.gradients.gradient2}`,
          color: theme.palette.common.white,
        },

    '& > em': {
      background: theme.palette.gradients.gradient1,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      '&::before': {
        padding: 0,
        display: 'none',
        p: 0,
      },
      '& > strong': {
        background: theme.palette.gradients.gradient4,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'inherit',
        textTransform: 'inherit',
        padding: 'inherit',
        lineHeight: 'inherit',
        borderRadius: 'inherit',
        verticalAlign: 'inherit',
        boxSizing: 'inherit',
        whiteSpace: 'inherit',
        fontSize: 'inherit',
        position: 'inherit',
        fontStyle: 'inherit',
        fontWeight: 'inherit',
        marginTop: 'inherit',
        '&::before': {
          padding: 0,
          display: 'none',
          p: 0,
        },
      },
    },
  },
  ...getTypographyModifierClasses(theme),
});

export function createSiteTheme(broker = false) {
  const theme = createTheme({
    cssVariables: broker
      ? false
      : {
          cssVarPrefix: 'template',
        },
    palette: {
      divider: '#E0E0E0',
      common: {
        black: '#200B42',
        white: '#ffffff',
      },
      text: {
        primary: '#200B42',
        secondary: '#3B3C4A',
      },
      brand: {
        dark: '#200B42',
        light: '#FFFFFF',
        brandDark: '#30219C',
        brand: '#3168FD',
        primaryDark: '#2A237E',
        primary: '#5342DC',
        primarySecondary1: '#8968FD',
        primarySecondary2: '#9747FF',
        primarySecondary3: '#AF3FD4',
        secondary: '#CB43AD',
        secondary1: '#E24888',
        magentaGlow: '#8a127a',
        pinkHalo: '#ff6ad5',
        error: '#FF4B4B',
        green: '#04CF88',
        greenLight: '#E2FCE7',
        gray: '#262626',
        grayLight: '#5C5C5C',
        bodyText: '#05030a',
      },
      purple: {
        900: '#1F0D3D',
        800: '#29124E',
        700: '#422570',
        650: '#6A57A4',
        600: '#9289D8',
        500: '#B3AADF',
        400: '#C9C2E9',
        300: '#E0D8ED',
        200: '#E9E9FC',
        100: '#F5F5FF',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 750,
        md: 900,
        lg: 1100,
        xl: 1200,
        xxl: 1450,
      },
    },
  });

  // Broker landing page: mirror the partners site (staging-partners.lend.com.au) palette.
  // Keys keep their CMS meaning (purple-800 = dark band, purple-400 = light band, ...)
  // so existing background choices in Strapi still render, just in the blue scale.
  if (broker) {
    Object.assign(theme.palette.brand, {
      dark: '#16181B',
      brandDark: '#1F34C9', // links / button text on white
      brand: '#304FF8',
      primaryDark: '#304FF8',
      primary: '#304FF8', // primary buttons & hero / CTA bands
      primarySecondary1: '#BCC9FF', // headline highlight on blue
      primarySecondary2: '#B9C4F8',
      primarySecondary3: '#2540E0',
      secondary: '#BCC9FF',
      secondary1: '#E2E8FF', // borders / light accents
      magentaGlow: '#2540E0',
      pinkHalo: '#BCC9FF',
    });
    Object.assign(theme.palette.purple, {
      900: '#081444', // navy tagline strip
      800: '#304FF8', // blue bands
      700: '#1F34C9',
      650: '#2540E0', // hover blue
      600: '#B9C4F8',
      500: '#BCC9FF',
      400: '#F1F4FF', // light bands / feature cards
      300: '#EEF1FF', // white-button hover
      200: '#E2E8FF',
      100: '#F7F9FF',
    });
    theme.palette.common.black = '#16181B';
    theme.palette.text.primary = '#16181B';
    theme.palette.text.secondary = '#4A4A4A';
    theme.palette.primary.main = '#304FF8';
    theme.palette.primary.dark = '#2540E0';
    theme.palette.primary.light = '#B9C4F8';
  }

  theme.palette.gradients = {
    gradient1: `linear-gradient(90deg, ${theme.palette.brand.primarySecondary1} 0%, ${theme.palette.brand.secondary1} 100%)`,
    gradient2: `linear-gradient(90deg, ${theme.palette.brand.primarySecondary3} 0%, ${theme.palette.brand.primary} 100%)`,
    gradient3: `radial-gradient(at 15% 0%, ${theme.palette.brand.secondary}DD 0%, transparent 70%),
   radial-gradient(at 85% 0%, ${theme.palette.brand.primary}DD 0%, transparent 70%)`,
    gradient4: `linear-gradient(90deg, ${theme.palette.brand.primary} 0%, ${theme.palette.brand.primarySecondary3} 120%)`,
    gradientCta: `linear-gradient(89deg, ${theme.palette.brand.secondary} 0.88%, ${theme.palette.brand.secondary1} 99.4%)`,
    primaryDark: `linear-gradient(180deg, ${theme.palette.brand.primaryDark} 35%, ${theme.palette.brand.secondary} 400%)`,
    purpleWhite: `linear-gradient(55deg, ${theme.palette.purple[200]} -10%, ${theme.palette.common.white} 80%)`,
    purpleLight: `linear-gradient(55deg, ${theme.palette.purple[200]} -10%, ${theme.palette.purple[500]} 80%)`,
    purpleDark: `linear-gradient(180deg, ${theme.palette.purple[900]} 35%, ${theme.palette.purple[800]} 100%)`,
    reviewCard: `linear-gradient(${theme.palette.purple[broker ? 900 : 800]}, ${theme.palette.purple[broker ? 900 : 800]}), linear-gradient(to left, ${theme.palette.brand.secondary1} 0%, ${theme.palette.brand.primarySecondary1} 100%)`,
    secondaryBrand: `
    radial-gradient(90% 85% at -15% 20%, ${theme.palette.brand.secondary} -45%,transparent 80%),
    radial-gradient(50% 65% at 0% 105%, ${theme.palette.brand.secondary1} -45%,transparent 80%),
    radial-gradient(45% 70% at 100% 15%, ${theme.palette.brand.primarySecondary2} -45%,transparent 80%),
    radial-gradient(150% 70% at -5% 35%, ${theme.palette.purple[700]} -45%,transparent 80%),
    radial-gradient(150% 60% at 105% 75%, ${theme.palette.brand.brandDark} -45%,transparent 80%),
    linear-gradient(115deg, ${theme.palette.purple[900]}, ${theme.palette.purple[800]}, ${theme.palette.purple[900]})
  `,
    heroText: `
    linear-gradient(180deg,
      ${alpha(theme.palette.brand.brandDark, 0)} 0%,
      ${alpha(theme.palette.brand.brandDark, 1)} 8%,
      ${alpha(theme.palette.brand.brandDark, 1)} 35%,
      ${alpha(theme.palette.brand.brandDark, 0)} 100%
    )
    `,
    imageOverlay: `
    linear-gradient(180deg,
      ${alpha(theme.palette.purple[700], 0.2)} 40%,
      ${alpha(theme.palette.purple[700], 0.6)} 75%
    ),
    linear-gradient(180deg, 
      ${alpha(theme.palette.purple[700], 0.05)} 0%,
      ${alpha(theme.palette.purple[700], 0.95)} 100%
    )
  `,
    imageOverlayThumbnail: `
    linear-gradient(180deg,
      ${alpha(theme.palette.purple[700], 0)} 45%,
      ${alpha(theme.palette.purple[700], 0.2)} 52%,
      ${alpha(theme.palette.purple[700], 0.9)} 65%
    ),
    linear-gradient(180deg, 
      ${alpha(theme.palette.purple[700], 0.1)} 0%,
      ${alpha(theme.palette.purple[700], 0.6)} 100%
    )
  `,
  };

  theme.shadows = {
    ...theme.shadows,
    a: `1px 1px 8px 0 ${theme.palette.purple[500]}`,
    b: `1px 1px 1rem 0 ${theme.palette.purple[500]}`,
    c: `1px 1px 2.5rem 0 ${theme.palette.purple[500]}`,
    d: `1px 1px 2rem 0 ${theme.palette.purple[500]}`,
  } as CustomShadows;

  theme.typography = {
    ...theme.typography,
    htmlFontSize: 16,
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontFamily: `var(--font-lato), ${lato.style.fontFamily}, sans-serif`,
    ...getTypographyModifierClasses(theme),

    button: {
      textTransform: 'none',
      fontSize: 'clamp(1rem, 0.4vw + 0.5rem, 1.25rem)',
      lineHeight: 1.5,
      fontWeight: 500,
    },

    display: {
      fontSize: 'clamp(3rem, 4vw + 1rem, 6rem)',
      lineHeight: 1.15,
      fontWeight: 700,
    },
    h1: {
      fontSize: 'clamp(2.25rem, 2.2vw + 1rem, 3.75rem)',
      lineHeight: 1.2,
      fontWeight: 700,
      whiteSpace: 'pre-line',
      ...getHeadingDecorators(theme),
    },
    h2: {
      fontSize: 'clamp(1.875rem, 1.8vw + 1rem, 3rem)',
      lineHeight: 1.25,
      fontWeight: 600,
      whiteSpace: 'pre-line',
      ...getHeadingDecorators(theme),
    },
    h3: {
      fontSize: 'clamp(1.5rem, 1.4vw + 0.8rem, 2.25rem)',
      lineHeight: 1.3,
      fontWeight: 500,
      whiteSpace: 'pre-line',
      ...getHeadingDecorators(theme),
    },
    h4: {
      fontSize: 'clamp(1.25rem, 0.9vw + 0.7rem, 1.75rem)',
      lineHeight: 1.35,
      fontWeight: 500,
      ...getHeadingDecorators(theme),
    },
    h5: {
      fontSize: 'clamp(1.125rem, 0.6vw + 0.6rem, 1.5rem)',
      lineHeight: 1.4,
      fontWeight: 500,
      ...getHeadingDecorators(theme),
    },
    h6: {
      fontSize: 'clamp(1rem, 0.4vw + 0.5rem, 1.25rem)',
      lineHeight: 1.4,
      fontWeight: 500,
      ...getHeadingDecorators(theme, true),
    },

    subtitle1: {
      fontSize: 'clamp(1.25rem, 0.6vw + 0.8rem, 1.65rem)',
      lineHeight: 1.45,
      ...gradientTextCss(theme),
    },
    subtitle2: {
      fontSize: 'clamp(1.125rem, 0.5vw + 0.7rem, 1.45rem)',
      lineHeight: 1.5,
      fontWeight: 700,
      color: theme.palette.purple[100],
    },

    // --- Labels ---
    label1: {
      fontSize: 'clamp(1rem, 0.4vw + 0.6rem, 1.15rem)',
      lineHeight: 1.4,
      fontWeight: 700,
      letterSpacing: '0.18px',
      color: theme.palette.purple[800],
    },
    label2: {
      fontSize: 'clamp(1rem, 0.4vw + 0.6rem, 1.15rem)',
      lineHeight: 1.4,
      fontWeight: 600,
      color: theme.palette.brand.primary,
      background: theme.palette.purple[100],
      borderRadius: 16,
      padding: '6px 12px',
      display: 'inline-block',
      breakAfter: 'always',
    },

    // --- Body Text ---
    body1: {
      fontSize: 'clamp(1.1rem, 0.6vw + 0.7rem, 1.45rem)',
      fontWeight: 400,
      fontFamily: `var(--font-lato), ${lato.style.fontFamily}, sans-serif`,
    },
    body2: {
      fontSize: 'clamp(1.05rem, 0.55vw + 0.65rem, 1.35rem)',
      fontWeight: 400,
    },

    // --- Pills ---
    pill: {
      display: 'inline-block',
      textTransform: 'uppercase',
      padding: '0.35em 0.7em',
      lineHeight: 1.3,
      fontSize: 'clamp(0.85rem, 0.3vw + 0.55rem, 1rem)',
      fontWeight: 700,
      background: theme.palette.purple[800],
      borderRadius: '30rem',
      verticalAlign: 'middle',
      boxSizing: 'content-box',
      strong: {
        background: theme.palette.gradients.gradient1,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontStyle: 'normal',
        fontWeight: 700,
      },
    },
    pill2: {
      display: 'inline-block',
      textTransform: 'uppercase',
      padding: '0.35em 0.7em',
      lineHeight: 1.3,
      fontSize: 'clamp(0.85rem, 0.3vw + 0.55rem, 1rem)',
      fontWeight: 700,
      borderRadius: '500px',
      verticalAlign: 'middle',
      background: broker
        ? theme.palette.gradients.gradient2
        : '-webkit-linear-gradient(0deg, rgba(175,63,212,1) 0%, rgba(83,66,220,1) 100%)',
      strong: {
        fontStyle: 'normal',
        color: theme.palette.common.black,
      },
    },

    caption: {
      fontSize: 'clamp(0.9rem, 0.3vw + 0.55rem, 1rem)',
      lineHeight: 1.5,
      textAlign: 'center',
    },
    tiny: {
      fontSize: 'clamp(0.8rem, 0.25vw + 0.45rem, 0.9rem)',
      lineHeight: 1.5,
    },
    small: {
      fontSize: 'clamp(0.9rem, 0.3vw + 0.5rem, 1rem)',
      lineHeight: 1.5,
    },
    medium: {
      fontSize: 'clamp(1.1rem, 0.35vw + 0.55rem, 1.3rem)',
      lineHeight: 1.3,
    },
    large: {
      fontSize: 'clamp(1.25rem, 0.5vw + 0.7rem, 1.65rem)',
      lineHeight: 1.5,
    },
    muted: {
      fontSize: 'clamp(0.9rem, 0.3vw + 0.5rem, 1rem)',
      color: theme.palette.brand.grayLight,
      lineHeight: 1.6,
    },
  };

  theme.components = {
    ...theme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
          lineHeight: 1.75,
        },
        p: {
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
          lineHeight: 1.75,
          marginTop: 0,
          marginBottom: '1.25em',
          color: theme.palette.text.primary,
        },
        ul: {
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
          lineHeight: 1.75,
          paddingLeft: '1.5em',
          marginTop: 0,
          marginBottom: '1.25em',
        },
        ol: {
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
          lineHeight: 1.75,
          paddingLeft: '1.5em',
          marginTop: 0,
          marginBottom: '1.25em',
        },
        li: {
          marginBottom: '0.4em',
        },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true },
      styleOverrides: {
        root: {
          '::before': { display: 'none' },
          padding: '1rem',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontFamily: `var(--font-lato), ${lato.style.fontFamily}, sans-serif`,
          fontWeight: 'bold',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default', elevation: 0 },
      styleOverrides: {
        colorDefault: {
          backgroundColor: theme.palette.common.white,
          border: 'none',
          color: theme.palette.common.black,
          padding: '1rem',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'flat' },
          style: {
            borderRadius: '3.125rem',
            fontSize: '1.125rem',
            fontWeight: 700,
            backgroundColor: theme.palette.brand.primary,
            color: theme.palette.common.white,
            padding: '0.75rem 1.5rem',
          },
        },
      ],
      styleOverrides: {
        root: {
          fontWeight: 700,
          textTransform: 'none',
        },
        sizeSmall: {
          padding: '0.5rem 1rem',
        },
        sizeMedium: {
          padding: '0.75rem 1.5rem',
        },
        sizeLarge: {
          padding: '1rem 2rem',
        },
        outlined: {
          borderRadius: '3.125rem',
          borderColor: theme.palette.purple[500],
          padding: '0.75rem 1.5rem',
          ':hover': { backgroundColor: theme.palette.purple[300] },
        },
        contained: {
          borderRadius: '3.125rem',
          color: theme.palette.common.white,
          background: broker ? theme.palette.brand.primary : theme.palette.gradients.gradientCta,
          boxShadow: 'none',
          transition: 'all ease 200ms',
          padding: '0.75rem 1.5rem',
          ':hover,:focus': {
            background: broker ? theme.palette.primary.dark : theme.palette.gradients.gradient1,
            boxShadow: (theme.shadows as CustomShadows).a,
          },
          '&.Mui-disabled': { background: theme.palette.purple[300] },
        },
        text: {
          padding: '0.5rem 1rem',
          ':hover': { background: 'none', textDecoration: 'underline' },
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'outlined-fine' },
          style: {
            border: `0.375rem solid ${theme.palette.purple[500]}`,
            backgroundColor: theme.palette.common.white,
            padding: '2rem',
          },
        },
      ],
      styleOverrides: { root: { borderRadius: 16, padding: '2rem' } },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.palette.brand.error,
          fontSize: '0.625rem',
          marginTop: '0.25rem',
          fontFamily: theme.typography.fontFamily,
        },
      },
    },
    MuiLink: { styleOverrides: { root: { color: theme.palette.brand.primary } } },
    MuiMenu: {
      defaultProps: { color: 'default' },
      styleOverrides: { list: { color: theme.palette.common.black } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: theme.palette.common.white,
          borderRadius: 4,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.purple[500],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.purple[600],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.brand.primary,
          },
        },
        input: {
          paddingBlock: theme.spacing(1.558),
          paddingInline: theme.spacing(2),
          '&::placeholder': {
            color: broker ? theme.palette.brand.grayLight : theme.palette.purple[500],
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          background: 'none',
          fontFamily: `var(--font-lato), ${lato.style.fontFamily}, sans-serif`,
          padding: '2rem',
        },
        outlined: {
          borderRadius: 24,
          border: '3px solid transparent',
          backgroundColor: theme.palette.common.white,
          borderImage: `${GradientText} 1`,
          padding: '2rem',
        },
      },
    },
  };

  if (broker) theme.palette.gradients.primaryDark = theme.palette.brand.primaryDark;
  return theme;
}

export default createSiteTheme();
